// PR NOTES: rename to WorkflowTrackerState
import React, { useState, useEffect, useRef, useMemo } from "react"
import { useParams } from "react-router-dom"
import dayjs from "dayjs"

// components
import { Tooltip, Popover, Form } from "antd"
import { UserBadge } from "components-design-system"
import ConfirmChangesModal from "../ConfirmChangesModal/ConfirmChangesModal"
import GhostState from "../GhostState/GhostState"
import MilestoneMarker from "../MilestoneMarker/MilestoneMarker"
import StepFormModal from "./subcomponents/StepFormModal"

// apis
import { useUserSettings } from "api/hooks"

// context
import useWorkflowTracker from "context/hooks/useWorkflowTracker"
import useAppState from "context/hooks/useAppState"
import useWorkflowTeam from "components/WorkflowModalForm/utils/useWorkflowTeam"

// styles
import "./WorkflowTrackerState.scss"

const isBetween = require("dayjs/plugin/isBetween")
dayjs.extend(isBetween)

const WorkflowTrackerState = (props) => {
  const { activityId } = useParams() // vs requestId
  const workflowType = activityId ? "activity" : "request"

  const {
    tempTrackerData,
    state,
    index,
    isFirst,
    isLast,
    pizzaTrackerRef,
    dateFormat,
    onSaveStateDates,
    onRevertStateDates,
    showAssignees,
    showDueDates,
    showDays,
    trackerMarginTop,
    setTrackerMarginTop,
    milestones = [],
    isToday,
    readOnly,
    invalidateParentQueries,
  } = props

  const [isDragging, setIsDragging] = useState(false)
  const [originalXPos, setOriginalXPos] = useState(0)
  const [newXPos, setNewXPos] = useState(null)
  const [openConfirmDateChangeModal, setConfirmDateChangeModal] = useState(false)
  const [showGhostState, setShowGhostState] = useState(false)
  const [showStateClicked, setShowStateClicked] = useState(false)
  const [showStateFormModal, setShowStateFormModal] = useState(false)
  const [ghostState, setGhostState] = useState({ ...props.state })
  const { data: userSettings } = useUserSettings()

  // Ant Design Form
  const [form] = Form.useForm()
  const stateStartDate = Form.useWatch("start_date", form)
  const stateEndDate = Form.useWatch("end_date", form)

  React.useEffect(() => {}, [stateStartDate, stateEndDate])

  const { isWorkflowComplete, isWorkflowCancelled, isOnHold } = useWorkflowTracker()
  const { teamOptions } = useWorkflowTeam()
  const { openToastNotification } = useAppState()

  const isStateComplete = state?.status === "Completed"
  const isStateBypassed = state?.status === "Bypassed"

  const isComplete = useMemo(() => {
    return isStateComplete || isStateBypassed
  }, [isStateComplete, isStateBypassed])

  let currentBarRef = useRef()

  let newXPosRef = useRef()
  let setNewXPosRef = useRef()

  let originalXPosRef = useRef()
  let setOriginalXPosRef = useRef()

  newXPosRef.current = newXPos
  setNewXPosRef.current = setNewXPos
  setOriginalXPosRef.current = setOriginalXPos

  let ghostStateRef = useRef()

  const onLongPress = (e) => {
    if (isWorkflowCancelled !== true && readOnly !== true && isStateBypassed !== true) {
      setShowStateClicked(true)
      setIsDragging(true)
      setOriginalXPos(e.screenX)
      originalXPosRef.current = e.screenX
      pizzaTrackerRef.current.style.cursor = "grabbing"
    }
  }

  useEffect(() => {
    if (isWorkflowCancelled !== true && readOnly !== true) {
      if (isDragging === true) {
        pizzaTrackerRef.current?.addEventListener("mouseup", onLongPressRelease, true)
        pizzaTrackerRef.current?.addEventListener("mousemove", handleMouseMove, true)
      } else {
        pizzaTrackerRef.current?.removeEventListener("mouseup", onLongPressRelease, true)
        pizzaTrackerRef.current?.removeEventListener("mousemove", handleMouseMove, true)
      }
    }

    return () => {
      pizzaTrackerRef.current?.removeEventListener("mouseup", onLongPressRelease, true)
      pizzaTrackerRef.current?.removeEventListener("mousemove", handleMouseMove, true)
    }
  }, [isDragging])

  const endDate = useMemo(() => {
    return dayjs(state.end_state_date)
  }, [state])

  const startDate = useMemo(() => {
    return dayjs(state.start_state_date)
  }, [state])

  const isLate = useMemo(() => {
    let today = dayjs()

    if (state.is_late || (state.is_current && today.isAfter(endDate, "day"))) {
      return true
    }

    if (state.is_current) {
      let today = dayjs()

      if (today.isBetween(startDate, endDate) || endDate.format("YYYY-MM-DD") === today.format("YYYY-MM-DD")) {
        let daysUntilDue = endDate.diff(today, "day")
        if (daysUntilDue < 20) return true
      }
    }

    return false
  }, [state])

  const totalDays = state.goal_days

  const onLongPressRelease = () => {
    setIsDragging(false)
    setShowStateClicked(false)
    pizzaTrackerRef.current.style.cursor = "inherit"
    setConfirmDateChangeModal(true)
  }

  const dragState = (state, oldPos, newPos, final) => {
    if (showGhostState === false) setShowGhostState(true)

    let updatedState = { ...state }
    let diffDays = oldPos - newPos

    let numDaysForActivity = Math.floor(Math.abs(diffDays) / 6)

    let ghostStart = startDate
    let ghostEnd = endDate

    if (diffDays < 0) {
      // move forward
      let duration = ghostEnd.diff(ghostStart, "day")
      ghostStart = ghostStart.add(numDaysForActivity, "day")
      ghostEnd = ghostStart.add(duration, "day")
      updatedState.start_state_date = ghostStart.format("YYYY-MM-DD")
      updatedState.end_state_date = ghostEnd.format("YYYY-MM-DD")

      setGhostState(updatedState)
      ghostStateRef.current = updatedState
    } else {
      // move back
      ghostStart = ghostStart.subtract(numDaysForActivity, "day")
      updatedState.start_state_date = ghostStart.format("YYYY-MM-DD")
      setGhostState(updatedState)
      ghostStateRef.current = updatedState
    }
  }

  let sortedMilestones = useMemo(() => {
    if (!milestones) return []
    return milestones
      ?.filter((a) => a.title !== "Created Date")
      ?.sort((a, b) => {
        return new Date(dayjs(a.value).format("YYYY-MM-DD")) - new Date(dayjs(b.value).format("YYYY-MM-DD"))
      })
  }, [milestones])

  let milestoneOffsets = useMemo(() => {
    if (sortedMilestones) {
      let res = []

      sortedMilestones.forEach((milestone) => {
        let milestoneDate = dayjs(milestone.value)
        let milestoneFromStart = milestoneDate.diff(startDate, "day")

        let milestoneMarkerOffset = Math.ceil((milestoneFromStart / totalDays) * 100)
        if (isNaN(milestoneMarkerOffset)) res.push(0)
        else res.push(milestoneMarkerOffset)
      })

      return res
    }

    return []
  }, [sortedMilestones])

  const title = useMemo(() => {
    if (state?.state_title === "Cancelled") return ""

    return state?.state_title.replace("and", "&")
  }, [state?.state_title])

  const handleMouseMove = (event) => {
    setNewXPosRef.current(event.screenX)

    dragState(state, originalXPos, event.screenX, false)
  }

  const getBubbleClassNames = () => {
    if (isLast) {
      return ""
    }

    let classNames = []
    if (isWorkflowComplete) return `bubble-complete bubble-activity-complete`
    if (isWorkflowCancelled || readOnly || isStateBypassed) return `no-grab`

    if (isStateComplete || isStateBypassed || state.is_current) {
      classNames.push(`bubble-complete`)
    }

    if (classNames.length === 0) return ""

    return classNames.join(" ")
  }

  const getCurrentProgressBarClassNames = () => {
    let classNames = []
    if (isWorkflowComplete) return `progress-complete`
    if (isLast) return classNames.join(" ")

    if (isLate) classNames.push("progress-late")
    if (isWorkflowCancelled) {
      classNames.push("progress-full-width")
      classNames.push("progress-late")
    }

    if (isOnHold) classNames.push("progress-onhold")
    else if (state.is_current) {
      let today = dayjs()
      let endDate = dayjs(state.end_state_date)

      classNames.push("current-progress-bar")

      if (today.isBetween(startDate, endDate) || endDate.format("YYYY-MM-DD") === today.format("YYYY-MM-DD")) {
        let daysUntilDue = endDate.diff(today, "day")
        if (daysUntilDue < 20) classNames.push("progress-late")
      }
    }

    if (classNames.length === 0) return ""
    return classNames.join(" ")
  }

  const getDiamondClassNames = () => {
    let classNames = []

    if (isOnHold) {
      classNames.push("hide")
    } else if (isLate || isWorkflowCancelled) {
      classNames.push("diamond-late")
      return classNames
    }

    if (state.is_current) {
      let today = dayjs()

      if (today.isBetween(startDate, endDate) || endDate.format("YYYY-MM-DD") === today.format("YYYY-MM-DD")) {
        let daysUntilDue = endDate.diff(today, "day")
        if (daysUntilDue < 20) classNames.push("diamond-late")
      }
    }
    return classNames.join(" ")
  }

  const getCurrentBarStyle = () => {
    let style = {}
    if (isOnHold || isWorkflowCancelled || isWorkflowComplete) {
      style.width = "100%"
      return style
    } else if (isWorkflowComplete && isLast) {
      style.width = "0px"
      return style
    }

    let today = dayjs()

    if (today.isBetween(startDate, endDate)) {
      if (today.isSame(startDate, "day")) {
        style.width = `20px`
        return style
      }
      let todayFromStart = today.diff(startDate, "day")
      let percent = (todayFromStart / totalDays) * 100
      style.width = `calc(${percent}% + 12px)`
      return style
    }

    style.width = "100%"
    return style
  }

  const renderMarkers = (milestoneOffsets) => {
    return sortedMilestones.map((milestone, i) => {
      return (
        <MilestoneMarker
          key={i + "-milestone-date"}
          milestoneIndex={i}
          milestone={milestone}
          milestones={sortedMilestones}
          isLast={isLast}
          isFirst={index === 0}
          state={state}
          stateIndex={index}
          tempTrackerData={tempTrackerData}
          offset={milestoneOffsets[i]}
          milestoneOffsets={milestoneOffsets}
          milestoneMarkerIndex={i}
          trackerMarginTop={trackerMarginTop}
          setTrackerMarginTop={setTrackerMarginTop}
        />
      )
    })
  }

  let todayMarkerOffset = useMemo(() => {
    let offset = 0
    let daysFromStart = null

    if (isToday) {
      daysFromStart = dayjs().diff(startDate, "day")
      offset = Math.ceil((daysFromStart / totalDays) * 100)
    }

    return offset
  }, [state, isToday])

  let dragStateLeft = -8
  let dragLeftDiff = (originalXPos - newXPos) * -1

  const draggableStateStyle = {
    left: dragStateLeft + dragLeftDiff,
  }

  const renderBubble = () => {
    if (isFirst === false) {
      let prevState = tempTrackerData[index - 1]

      if (prevState.is_current) {
        if (prevState.is_onhold) {
          return (
            <div
              className={`bubble-style ${readOnly ? "remove-bubble-style" : ""} ${
                isDragging && "dragging-step"
              } ${getBubbleClassNames()} ${showStateClicked && "show-bubble-outline"}`}
              onMouseDown={onLongPress}
              style={{ border: "none" }}
            >
              <i
                className="fal fa-pause-circle onhold-icon"
                style={{ fontSize: 22 }}
              />
            </div>
          )
        } else if (prevState.state_title === "Cancelled") {
          return (
            <div
              className={`bubble-style ${readOnly ? "disable-bubble-style" : ""} ${
                isDragging && "dragging-step"
              } ${getBubbleClassNames()} ${showStateClicked && "show-bubble-outline"}`}
              onMouseDown={onLongPress}
              style={{ border: "none" }}
            >
              <i
                className="fal fa-times-circle cancelled-icon"
                style={{ fontSize: 22 }}
              />
            </div>
          )
        }
      }
    }

    return (
      <div
        className={`bubble-style ${readOnly ? "disable-bubble-style" : ""} ${
          isDragging && "dragging-step"
        } ${getBubbleClassNames()} ${showStateClicked && "show-bubble-outline"}`}
        onMouseDown={onLongPress}
      />
    )
  }

  const renderLastBubble = () => {
    if (isWorkflowCancelled) {
      return (
        <i
          className={`${getBubbleClassNames()} ${
            showStateClicked && "show-last-bubble-dragging"
          } fal fa-times-circle  ${readOnly ? "disable-last-bubble-icon" : "last-bubble-icon last-bubble"}`}
          onMouseDown={onLongPress}
          style={{ color: "#D7542C" }}
        />
      )
    }

    return isWorkflowComplete ? (
      <div className={`last-bubble last-bubble-complete ${isDragging && "dragging-step"}`}>
        <i className="fal fa-check" />
      </div>
    ) : (
      <i
        className={`${getBubbleClassNames()} ${
          showStateClicked && "show-last-bubble-dragging"
        } fal fa-check-circle last-bubble  ${readOnly ? "disable-last-bubble-icon " : "last-bubble-icon last-bubble"}}`}
        onMouseDown={onLongPress}
      />
    )
  }

  const closeGhostState = () => {
    setShowGhostState(false)
    setOriginalXPosRef.current(newXPosRef.current)
    originalXPosRef.current = newXPosRef.current
  }

  const renderDays = () => {
    let classNames = ["num-of-days", "num-of-days-other"]

    if (isOnHold || isStateBypassed || !showDays || (isWorkflowCancelled === true && state.is_current)) {
      classNames.push("hide")
    }

    if (isWorkflowComplete) {
      classNames.push("num-of-days-activity-complete")
    } else if (isStateComplete || isStateBypassed) {
      classNames.push("num-of-days-complete")
    }

    return <p className={classNames.join(" ")}>{totalDays}</p>
  }

  const renderDiamond = () => {
    return (
      <div
        id="diamond"
        className={getDiamondClassNames()}
        style={{
          right: -8.5,
          marginLeft: "auto",
        }}
      />
    )
  }

  const handleEditStateModal = () => {
    if (isWorkflowCancelled) {
      openToastNotification("error", "Error", `This ${workflowType} is cancelled. You cannot edit the step.`)
      return
    }

    if (isWorkflowComplete) {
      openToastNotification("error", "Error", `This ${workflowType} is completed. You cannot edit the step.`)
      return
    }

    if (readOnly !== true) {
      setShowStateFormModal((prev) => !prev)
    }
  }

  return (
    <>
      <div
        className={`workflow-tracker-state ${!readOnly ? "enable-hover-styles" : ""} ${isLast && "last-step"} ${
          isOnHold && "onhold-background"
        } `}
      >
        {isFirst && (
          <div
            className={`first-bubble ${isDragging && "dragging-step"} ${getBubbleClassNames()}`}
            onMouseDown={onLongPress}
          >
            <i className="fas fa-chevron-right" />
          </div>
        )}
        {!isLast && !isFirst && renderBubble()}
        {isLast && renderLastBubble()}

        {showGhostState && (
          <Popover
            trigger="click"
            title="Are you sure you want to save changes?"
            open={openConfirmDateChangeModal}
            content={
              <ConfirmChangesModal
                state={state}
                ghostState={ghostState}
                closeGhostState={closeGhostState}
                onSaveStateDates={(ghostState) => {
                  closeGhostState()
                  onSaveStateDates(ghostState)
                }}
                onRevertStateDates={() => {
                  closeGhostState()
                  onRevertStateDates()
                }}
                openConfirmDateChangeModal={openConfirmDateChangeModal}
                setConfirmDateChangeModal={setConfirmDateChangeModal}
                dateFormat={userSettings?.date_format}
              />
            }
          >
            <div
              className="draggable-step"
              style={draggableStateStyle}
            >
              <GhostState
                ghostState={ghostState}
                setGhostState={setGhostState}
                showGhostState={showGhostState}
                setShowGhostState={setShowGhostState}
                index={index}
                isLast={isLast}
                dateFormat={dateFormat}
              />
              {showStateClicked && (
                <div id="arrowAnim-left">
                  <div className="arrowSliding">
                    <div className="arrow"></div>
                  </div>
                  <div className="arrowSliding delay1">
                    <div className="arrow"></div>
                  </div>
                  <div className="arrowSliding delay2">
                    <div className="arrow"></div>
                  </div>
                  <div className="arrowSliding delay3">
                    <div className="arrow"></div>
                  </div>
                </div>
              )}
              {showStateClicked && (
                <div id="arrowAnim-right">
                  <div className="arrowSliding">
                    <div className="arrow"></div>
                  </div>
                  <div className="arrowSliding delay1">
                    <div className="arrow"></div>
                  </div>
                  <div className="arrowSliding delay2">
                    <div className="arrow"></div>
                  </div>
                  <div className="arrowSliding delay3">
                    <div className="arrow"></div>
                  </div>
                </div>
              )}
            </div>
          </Popover>
        )}

        <div className={`date-container ${isLast && "last-date-container"}`}>
          <div className={`date-line ${showGhostState && "date-line-showing-ghost-step"}`} />
          <div
            className="start-date-container no-text-highlight"
            style={{
              color: (isWorkflowComplete || isWorkflowCancelled || isStateComplete || isStateBypassed) && "#ADAFB0",
            }}
          >
            {isStateBypassed ? "Bypassed" : startDate.format(userSettings?.date_format)}
          </div>
        </div>

        {showDueDates && milestones && renderMarkers(milestoneOffsets)}

        <div
          className="status-bar-container"
          style={{ height: "16px", ...(readOnly ? {} : { cursor: "pointer" }) }}
          onClick={handleEditStateModal}
        >
          {renderDays()}

          {isComplete && <div className={`complete-progress-bar ${isWorkflowComplete && `progress-complete`}`}></div>}

          {!isLast && (
            <div className={`step-title-container ${isLast && "last-step-title-container"}`}>
              <Tooltip title={state.state_description ? state.state_description : null}>
                <div
                  className={`tracker-step-title no-text-highlight${
                    isWorkflowComplete || isWorkflowCancelled || isStateComplete || isStateBypassed
                      ? ` tracker-step-title-inactive`
                      : "tracker-step-title-active"
                  } `}
                >
                  {title}
                  {state.state_description && (
                    <>
                      &nbsp;
                      <i
                        className="fal fa-info-circle"
                        aria-hidden="true"
                      />
                    </>
                  )}
                </div>
              </Tooltip>
              <div
                className="assignee-container"
                style={isLast ? { justifyContent: "center" } : {}}
              >
                {showAssignees &&
                  state?.assigned_to_names
                    ?.filter((person) => person?.trim().length > 1)
                    .map((person, i) => {
                      return (
                        <UserBadge
                          key={i}
                          user={person}
                        />
                      )
                    })}
              </div>
            </div>
          )}
          {(state.is_current || isWorkflowComplete) && (
            <div
              className={`${getCurrentProgressBarClassNames()}`}
              style={getCurrentBarStyle()}
              ref={currentBarRef}
            >
              <div style={{ position: "relative", width: "100%" }}>{!isWorkflowComplete && renderDiamond()}</div>
            </div>
          )}
          {isToday && (
            <Tooltip title={`Today's date: ${dayjs().format(userSettings?.date_format)}`}>
              <i
                style={{
                  left: `calc(${todayMarkerOffset}% + 4px)`,
                }}
                className="fas fa-caret-down today-marker"
              />
            </Tooltip>
          )}
        </div>
      </div>
      <StepFormModal
        state={state}
        teamOptions={teamOptions}
        open={showStateFormModal}
        isOnHold={isOnHold}
        handleForm={handleEditStateModal}
        invalidateParentQueries={invalidateParentQueries}
      />
    </>
  )
}

export default WorkflowTrackerState
